// src/components/dashboard/livegrid/liveGridTiles.js
// ─────────────────────────────────────────────────────────────────────────────
// FlowSense MOW+ — Live Grid basemap catalog
//
// CARTO raster basemaps are served through the FlowSense CARTO API key:
//   https://basemaps.cartocdn.com/rastertiles/<style>/{z}/{x}/{y}{r}.png?key=<KEY>
// Set REACT_APP_CARTO_API_KEY in .env to override the key below.
// If the keyed endpoint starts failing (bad / expired key, quota), the map switches
// to the public CARTO endpoint for that style automatically (see `fallbackUrl`).
//
// Pure data + helpers (no React) so it can be unit-tested and reused (e.g. the
// dashboard alert map).
// ─────────────────────────────────────────────────────────────────────────────

export const CARTO_API_KEY = process.env.REACT_APP_CARTO_API_KEY || 'cb1_4avm_1_5bd6e706e63138d449b32f3e';

const CARTO_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>';
const ESRI_ATTRIBUTION = '&copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics, USDA, USGS, AeroGRID, IGN, and the GIS User Community';

/** Keyed CARTO URL (the FlowSense API key) + keyless fallback for the same style. */
const carto = (style) => ({
    url: `https://basemaps.cartocdn.com/rastertiles/${style}/{z}/{x}/{y}{r}.png${CARTO_API_KEY ? `?key=${CARTO_API_KEY}` : ''}`,
    fallbackUrl: `https://{s}.basemaps.cartocdn.com/rastertiles/${style}/{z}/{x}/{y}{r}.png`,
    attribution: CARTO_ATTRIBUTION,
    maxZoom: 20,
    provider: 'carto',
});

const esri = (service) => `https://server.arcgisonline.com/ArcGIS/rest/services/${service}/MapServer/tile/{z}/{y}/{x}`;

/**
 * Every basemap the Live Grid offers.
 * `dark` tells the UI to use light outlines / legend on top of the map.
 * `overlays` are extra tile layers drawn on top (labels / roads for imagery).
 */
export const TILE_LAYERS = Object.freeze({
    carto_dark: { label: 'Dark Matter', group: 'CARTO', dark: true, description: 'High-contrast night view — best for live alerts and the AI Lens.', ...carto('dark_all') },
    carto_dark_nolabels: { label: 'Dark (no labels)', group: 'CARTO', dark: true, description: 'Dark base without place names — markers and zones stand out.', ...carto('dark_nolabels') },
    carto_voyager: { label: 'Voyager', group: 'CARTO', dark: false, description: 'Modern colour street map — the everyday operations view.', ...carto('voyager') },
    carto_voyager_labels_under: { label: 'Voyager (labels under)', group: 'CARTO', dark: false, description: 'Voyager with labels drawn beneath roads for a cleaner network view.', ...carto('voyager_labels_under') },
    carto_positron: { label: 'Positron (light)', group: 'CARTO', dark: false, description: 'Light, low-noise base — ideal for printing and zone analytics.', ...carto('light_all') },
    carto_positron_nolabels: { label: 'Light (no labels)', group: 'CARTO', dark: false, description: 'Minimal light base without labels.', ...carto('light_nolabels') },

    satellite: { label: 'Satellite', group: 'Imagery', dark: true, description: 'Esri World Imagery — check site conditions, drainage and access.', url: esri('World_Imagery'), attribution: ESRI_ATTRIBUTION, maxZoom: 19, provider: 'esri' },
    hybrid: {
        label: 'Hybrid (satellite + labels)', group: 'Imagery', dark: true,
        description: 'Satellite imagery with roads and place names on top.',
        url: esri('World_Imagery'), attribution: ESRI_ATTRIBUTION, maxZoom: 19, provider: 'esri',
        overlays: [
            { url: esri('Reference/World_Transportation'), opacity: 0.9 },
            { url: esri('Reference/World_Boundaries_and_Places'), opacity: 0.95 },
        ],
    },

    streets: { label: 'Esri Streets', group: 'Streets & terrain', dark: false, description: 'Detailed street map with building footprints.', url: esri('World_Street_Map'), attribution: '&copy; Esri', maxZoom: 19, provider: 'esri' },
    topo: { label: 'Terrain', group: 'Streets & terrain', dark: false, description: 'OpenTopoMap contours — elevation and flood-prone low ground.', url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', attribution: 'Map data &copy; OpenStreetMap contributors, SRTM | Style &copy; <a href="https://opentopomap.org">OpenTopoMap</a>', maxZoom: 17, provider: 'opentopomap' },
    osm: { label: 'OpenStreetMap', group: 'Streets & terrain', dark: false, description: 'Standard OpenStreetMap.', url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors', maxZoom: 19, provider: 'osm' },
});

export const TILE_GROUPS = ['CARTO', 'Imagery', 'Streets & terrain'];

/** Old LiveGrid keys → new keys, so a saved preference keeps working. */
const LEGACY_KEYS = { voyager: 'carto_voyager', dark: 'carto_dark' };

export const TILE_STORAGE_KEY = 'flowSense_liveGridTiles';

export const resolveTileKey = (key) => {
    const k = LEGACY_KEYS[key] || key;
    return TILE_LAYERS[k] ? k : null;
};

/** Default basemap: the saved choice, else Dark Matter in dark mode / Voyager in light mode. */
export const defaultTileKey = (colorMode = 'light') => {
    try {
        const saved = resolveTileKey(window.localStorage.getItem(TILE_STORAGE_KEY));
        if (saved) return saved;
    } catch { /* private mode */ }
    return colorMode === 'dark' ? 'carto_dark' : 'carto_voyager';
};

export const saveTileKey = (key) => {
    try { window.localStorage.setItem(TILE_STORAGE_KEY, key); } catch { /* private mode */ }
};

/** Tiles that failed before ANY tile loaded → treat the keyed endpoint as broken. */
export const TILE_FAILURE_THRESHOLD = 6;

/** URL to use for a layer, honouring the automatic keyless fallback. */
export const tileUrlFor = (key, { useFallback = false } = {}) => {
    const layer = TILE_LAYERS[key];
    if (!layer) return null;
    return useFallback && layer.fallbackUrl ? layer.fallbackUrl : layer.url;
};
